/**
 * `schemas/health.ts` — `spec.md` §6.10.
 *
 * **`/healthz` is the one endpoint that is NOT wrapped in the `{ ok, data }` envelope.**
 * Its `ok` is a top-level boolean and the body is identical on `200` and on `503`. That
 * is deliberate: §6.10 says the endpoint "exists specifically to answer 'why does search
 * return nothing in prod' with one `curl`", and an envelope would bury the SQLite
 * version and the FTS5 flag one level deeper for no benefit.
 */
import * as z from 'zod';
import { NonEmptyText, UnixSeconds } from './common.js';
export const HealthzResponseSchema = z.strictObject({
    /** `false` when the DB is unopenable or a migration failed — with the same body. */
    ok: z.boolean(),
    /** `process.version`, e.g. `v24.20.0`. */
    node: NonEmptyText,
    /**
     * `sqlite_version()` of the build actually loaded. A runtime fact: Node 25 ships an
     * OLDER SQLite than Node 24 (3.51.3 vs 3.53.4), so this cannot be inferred from
     * `node` above (`api-groundtruth.md` §4).
     */
    sqlite: NonEmptyText,
    /** Result of the boot capability probe, not an assumption from the SQLite version. */
    fts5: z.boolean(),
    trigram: z.boolean(),
    walBytes: z.int().nonnegative(),
    tracks: z.int().nonnegative(),
    indexReady: z.boolean(),
    lastIndexSuccessAt: UnixSeconds.nullable(),
    cacheBytes: z.int().nonnegative(),
    uptimeS: z.int().nonnegative(),
    /** `1.0.0+abc1234` — package version plus the build's short commit. */
    version: NonEmptyText,
});
/**
 * `GET /metrics` is Prometheus **text**, not JSON, so its "schema" is a string. It is
 * declared rather than omitted so the endpoint manifest has no gaps.
 */
export const MetricsResponseSchema = z.string();
/** The exposition format `prom-client`'s registry emits. */
export const METRICS_CONTENT_TYPE = 'text/plain; version=0.0.4; charset=utf-8';
//# sourceMappingURL=health.js.map